<template>
  <div v-if="visible" class="css-editor-panel" role="dialog" aria-modal="true" aria-labelledby="css-editor-title" @keydown.esc.prevent="close">
    <div class="css-editor-header">
      <span id="css-editor-title" class="css-editor-title">{{ mode === 'app' ? 'App CSS' : mode === 'page' ? 'Page CSS' : 'CSS' }}{{ mode !== 'app' ? ': ' + componentUniqueTitle : '' }}</span>
      <span class="css-editor-type-badge">{{ mode === 'app' ? 'APP' : mode === 'page' ? 'PAGE' : componentType }}</span>
      <modal-help topic="css-editor" label="Help for this screen" />
      <button type="button" class="css-editor-close" aria-label="Close" @click="close">&times;</button>
    </div>

    <div class="css-editor-body">
      <button type="button" class="css-editor-help-toggle" :aria-expanded="helpVisible ? 'true' : 'false'" @click="helpVisible = !helpVisible">
        <font-awesome-icon :icon="helpVisible ? 'chevron-down' : 'chevron-right'" />
        CSS selectors reference
      </button>

      <div v-if="helpVisible" class="css-editor-help">
        <p>Text on a background should be readable at a glance. Do not remove focus outlines.</p>
        <div v-html="helpContent"></div>
      </div>

      <div class="css-editor-info">
        <small v-if="mode === 'app'">
          Your CSS applies to all pages in this app.<br>
          It is not scoped &mdash; selectors apply globally.<br>
          Example: <code>.table th { color: red; }</code>
        </small>
        <small v-else-if="mode === 'page'">
          Your CSS is automatically scoped to this page.<br>
          It applies to all views on the page.<br>
          Example: <code>.table th { color: red; }</code>
        </small>
        <small v-else>
          Your CSS is automatically scoped to this view.<br>
          Write selectors as if targeting the view directly.<br>
          Example: <code>.table th { color: red; }</code>
        </small>
      </div>

      <div class="css-ai-section">
        <button type="button" class="css-ai-header" :aria-expanded="aiVisible ? 'true' : 'false'" @click="aiVisible = !aiVisible">
          <font-awesome-icon :icon="aiVisible ? 'chevron-down' : 'chevron-right'" />
          Edit with AI
        </button>
        <div v-if="aiVisible" class="css-ai-body">
          <label class="visually-hidden" for="css-ai-prompt">Describe the CSS change</label>
          <textarea
            id="css-ai-prompt"
            ref="aiPromptTextarea"
            class="css-ai-prompt"
            v-model="aiPrompt"
            placeholder="Describe what you want to change, e.g. 'make the header dark blue with white text'"
            :disabled="aiLoading"
            @keydown.enter.meta="runAiEdit"
            @keydown.enter.ctrl="runAiEdit"
            rows="2"
          ></textarea>
          <div class="css-ai-image-row">
            <label class="css-ai-image-label" :class="{ disabled: aiLoading }">
              <font-awesome-icon icon="image" />
              {{ aiReferenceImage ? 'Replace image' : 'Attach reference image' }}
              <input
                ref="aiImageInput"
                type="file"
                accept="image/*"
                class="css-ai-image-input"
                :disabled="aiLoading"
                @change="onAiImageSelected"
              />
            </label>
            <button
              v-if="aiReferenceImage"
              class="css-ai-image-remove"
              @click="removeAiImage"
              :disabled="aiLoading"
              aria-label="Remove image"
            >&times;</button>
          </div>
          <div v-if="aiReferenceImage" class="css-ai-image-preview">
            <img :src="'data:image/png;base64,' + aiReferenceImage" alt="Reference" />
          </div>
          <button
            class="css-editor-btn css-ai-btn"
            @click="runAiEdit"
            :disabled="aiLoading || !aiPrompt.trim()"
          >
            <span v-if="aiLoading" class="css-ai-spinner"></span>
            {{ aiLoading ? aiStatusText : 'Generate CSS' }}
          </button>
          <div v-if="aiLoading || aiThinking" class="css-ai-progress" aria-live="polite">
            <div class="css-ai-status">{{ aiStatusText }}</div>
            <pre v-if="aiThinking" ref="aiThoughts" class="css-ai-thoughts">{{ aiThinking }}</pre>
          </div>
          <div v-if="aiError" class="css-ai-error">{{ aiError }}</div>
          <AiTokenUsage />
        </div>
      </div>

      <label class="visually-hidden" for="css-editor-textarea">CSS</label>
      <textarea
        id="css-editor-textarea"
        ref="cssTextarea"
        class="css-editor-textarea"
        v-model="cssText"
        @input="onCssInput"
        :placeholder="mode === 'app' ? '/* Enter custom CSS for this app */\n.table th {\n  background-color: #333;\n  color: white;\n}' : mode === 'page' ? '/* Enter custom CSS for this page */\n.table th {\n  background-color: #333;\n  color: white;\n}' : '/* Enter custom CSS for this view */\n.table th {\n  background-color: #333;\n  color: white;\n}'"
        spellcheck="false"
      ></textarea>
    </div>

    <div class="css-editor-footer">
      <button class="css-editor-btn css-editor-btn-save" @click="save">Save</button>
      <button class="css-editor-btn css-editor-btn-cancel" @click="close">Cancel</button>
    </div>
  </div>
</template>

<script>
import AiTokenUsage from './AiTokenUsage.vue'
import { publishAiTokenUsage } from '../lib/ai-token-usage.js'

const CSS_EDIT_TIMEOUT_MS = 60 * 60 * 1000

const cssHelpByType = {
  table: `
    <p><strong>Table view selectors:</strong></p>
    <ul>
      <li><code>.view-title</code> &mdash; view title, omitted when the title is empty</li>
      <li><code>.table</code> &mdash; the table element</li>
      <li><code>.table-responsive</code> &mdash; responsive wrapper</li>
      <li><code>.table th</code> &mdash; header cells</li>
      <li><code>.table td</code> &mdash; data cells</li>
      <li><code>.table thead</code> &mdash; header row group</li>
      <li><code>.table tbody</code> &mdash; body row group</li>
      <li><code>.table tr</code> &mdash; table rows</li>
      <li><code>.table-striped tbody tr:nth-of-type(odd)</code> &mdash; striped rows</li>
      <li><code>.table-hover tbody tr:hover</code> &mdash; hover state</li>
    </ul>
  `,
  cards: `
    <p><strong>Cards view selectors:</strong></p>
    <ul>
      <li><code>.view-title</code> &mdash; view title, omitted when the title is empty</li>
      <li><code>.cards-container</code> &mdash; outer container</li>
      <li><code>.cards-grid</code> &mdash; the CSS grid wrapper</li>
      <li><code>.card-item</code> &mdash; individual card</li>
      <li><code>.card-body</code> &mdash; card body (Bootstrap)</li>
      <li><code>.card-layout-row</code> &mdash; one row of fields on a card</li>
      <li><code>.card-layout-column</code> &mdash; one column in that row</li>
      <li><code>.card-field</code> &mdash; field container</li>
      <li><code>.card-field-label</code> &mdash; field label</li>
      <li><code>.card-field-value</code> &mdash; field value</li>
      <li><code>.card-link</code> &mdash; link inside card</li>
    </ul>
  `,
  chart: `
    <p><strong>Chart view selectors:</strong></p>
    <ul>
      <li><code>.chart-container</code> &mdash; outer container</li>
      <li><code>.view-title</code>, <code>.chart-title</code> &mdash; chart title, omitted when the title is empty</li>
      <li><code>.chart-wrapper</code> &mdash; canvas wrapper</li>
      <li><code>canvas</code> &mdash; the Chart.js canvas element</li>
    </ul>
    <p><em>Note: Chart.js renders to a canvas. CSS controls the container, title, and wrapper but not chart internals.</em></p>
  `,
  form: `
    <p><strong>Form view selectors:</strong></p>
    <ul>
      <li><code>.view-title</code> &mdash; view title, omitted when the title is empty</li>
      <li><code>form</code> &mdash; the form element</li>
      <li><code>.form-group</code> &mdash; input group wrapper</li>
      <li><code>.form-control</code> &mdash; input elements</li>
      <li><code>label</code> &mdash; labels</li>
      <li><code>.btn-primary</code> &mdash; submit button</li>
      <li><code>.mb-3</code> &mdash; field spacing</li>
    </ul>
  `,
  login: `
    <p><strong>Login view selectors:</strong></p>
    <ul>
      <li><code>.view-title</code> &mdash; view title, omitted when the title is empty</li>
      <li><code>form</code> &mdash; the login form</li>
      <li><code>.form-group</code> &mdash; input group wrapper</li>
      <li><code>.form-control</code> &mdash; input elements</li>
      <li><code>.btn-primary</code> &mdash; login button</li>
      <li><code>.text-danger</code> &mdash; error message</li>
    </ul>
  `,
  carousel: `
    <p><strong>Carousel view selectors:</strong></p>
    <ul>
      <li><code>.carousel</code> &mdash; carousel container</li>
      <li><code>.carousel-inner</code> &mdash; slides wrapper</li>
      <li><code>.carousel-item</code> &mdash; individual slide</li>
      <li><code>.carousel-caption</code> &mdash; caption overlay</li>
      <li><code>.carousel-control-prev</code>, <code>.carousel-control-next</code> &mdash; navigation</li>
    </ul>
  `,
  default: `
    <p>Write CSS selectors relative to this view's container.</p>
  `
}

const cssHelpPage = `
  <p><strong>Page-level selectors:</strong></p>
  <ul>
    <li><code>.container-fluid</code> &mdash; Bootstrap fluid container</li>
    <li><code>.row</code> &mdash; Bootstrap rows</li>
    <li><code>.col</code> &mdash; Bootstrap columns</li>
  </ul>
  <p><strong>Child view components:</strong></p>
  <ul>
    <li><code>.table</code> &mdash; table views</li>
    <li><code>.table th</code>, <code>.table td</code> &mdash; table cells</li>
    <li><code>.cards-grid</code> &mdash; cards grid</li>
    <li><code>.card-item</code> &mdash; individual cards</li>
    <li><code>.chart-container</code> &mdash; chart wrapper</li>
    <li><code>form</code> &mdash; form views</li>
    <li><code>.carousel</code> &mdash; carousel views</li>
  </ul>
  <p><strong>Typography:</strong></p>
  <ul>
    <li><code>h1</code>&ndash;<code>h6</code>, <code>p</code>, <code>a</code> &mdash; headings, paragraphs, links</li>
  </ul>
`

const cssHelpApp = `
  <p><strong>App-wide selectors:</strong></p>
  <ul>
    <li><code>.navbar</code> &mdash; navigation bar</li>
    <li><code>#page-container</code> &mdash; page content wrapper</li>
    <li><code>.container-fluid</code> &mdash; Bootstrap fluid container</li>
  </ul>
  <p><strong>All view components:</strong></p>
  <ul>
    <li><code>.table</code> &mdash; table views</li>
    <li><code>.table th</code>, <code>.table td</code> &mdash; table cells</li>
    <li><code>.cards-grid</code> &mdash; cards grid</li>
    <li><code>.card-item</code> &mdash; individual cards</li>
    <li><code>.chart-container</code> &mdash; chart wrapper</li>
    <li><code>form</code> &mdash; form views</li>
    <li><code>.carousel</code> &mdash; carousel views</li>
  </ul>
  <p><strong>Typography &amp; layout:</strong></p>
  <ul>
    <li><code>h1</code>&ndash;<code>h6</code>, <code>p</code>, <code>a</code> &mdash; headings, paragraphs, links</li>
    <li><code>.row</code>, <code>.col</code> &mdash; Bootstrap grid</li>
    <li><code>.btn</code>, <code>.btn-primary</code> &mdash; buttons</li>
  </ul>
  <p><em>Note: App CSS applies to all pages and is not scoped.</em></p>
`

export default {
  name: 'ViewCssEditor',
  components: { AiTokenUsage },
  data () {
    return {
      visible: false,
      cssText: '',
      originalCssText: '',
      componentUniqueTitle: '',
      componentType: '',
      mode: 'view',
      helpVisible: false,
      parentDesigner: null,
      _debounceTimer: null,
      aiVisible: false,
      aiPrompt: '',
      aiLoading: false,
      aiError: null,
      aiStatusText: 'Generating...',
      aiThinking: '',
      aiReferenceImage: null
    }
  },
  computed: {
    helpContent () {
      if (this.mode === 'app') return cssHelpApp
      if (this.mode === 'page') return cssHelpPage
      return cssHelpByType[this.componentType] || cssHelpByType.default
    }
  },
  methods: {
    open (component, parentDesigner, mode) {
      this.opener = document.activeElement
      this.mode = mode || 'view'
      this.componentUniqueTitle = component.uniqueTitle || component.title || ''
      this.componentType = component.type || ''
      this.cssText = component.customCss || ''
      this.originalCssText = this.cssText
      this.parentDesigner = parentDesigner
      this.visible = true
      this.$nextTick(() => {
        if (this.$refs.cssTextarea) {
          this.$refs.cssTextarea.focus()
        }
      })
    },
    close () {
      clearTimeout(this._debounceTimer)
      if (this.cssText !== this.originalCssText) {
        if (this.mode === 'app') {
          this.parentDesigner.sendAppCssToPreview(this.originalCssText)
        } else if (this.mode === 'page') {
          this.parentDesigner.sendPageCssToPreview(this.originalCssText)
        } else {
          this.parentDesigner.sendCssToPreview(
            this.componentUniqueTitle,
            this.originalCssText
          )
        }
      }
      this.visible = false
      this.restoreFocus()
    },
    onCssInput () {
      clearTimeout(this._debounceTimer)
      this._debounceTimer = setTimeout(() => {
        if (this.mode === 'app') {
          this.parentDesigner.sendAppCssToPreview(this.cssText)
        } else if (this.mode === 'page') {
          this.parentDesigner.sendPageCssToPreview(this.cssText)
        } else {
          this.parentDesigner.sendCssToPreview(
            this.componentUniqueTitle,
            this.cssText
          )
        }
      }, 300)
    },
    save () {
      clearTimeout(this._debounceTimer)
      if (this.mode === 'app') {
        this.parentDesigner.saveAppCss(this.cssText)
      } else if (this.mode === 'page') {
        this.parentDesigner.savePageCss(this.cssText)
      } else {
        this.parentDesigner.saveComponentCss(
          this.componentUniqueTitle,
          this.cssText
        )
      }
      this.originalCssText = this.cssText
      this.aiPrompt = ''
      this.aiReferenceImage = null
      this.aiError = null
      this.visible = false
      this.restoreFocus()
    },
    restoreFocus () {
      const opener = this.opener
      this.opener = null
      this.$nextTick(() => {
        if (opener && typeof opener.focus === 'function') opener.focus()
      })
    },
    onAiImageSelected (event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        const dataUrl = e.target.result
        this.aiReferenceImage = dataUrl.split(',')[1]
      }
      reader.readAsDataURL(file)

      if (this.$refs.aiImageInput) {
        this.$refs.aiImageInput.value = ''
      }
    },
    removeAiImage () {
      this.aiReferenceImage = null
    },
    scrollThoughts () {
      this.$nextTick(() => {
        const box = this.$refs.aiThoughts
        if (box) box.scrollTop = box.scrollHeight
      })
    },
    consumeCssEvents (buffer) {
      const parts = buffer.split('\n\n')
      const rest = parts.pop()

      for (const part of parts) {
        const lines = part.split('\n')
        let eventName = 'message'
        let data = ''

        for (const line of lines) {
          if (line.startsWith('event:')) eventName = line.slice(6).trim()
          if (line.startsWith('data:')) data += line.slice(5).trim()
        }

        if (!data) continue

        const payload = JSON.parse(data)

        if (eventName === 'thinking') {
          this.aiThinking += payload.text || ''
          this.scrollThoughts()
        } else if (eventName === 'status') {
          this.aiStatusText = payload.text || this.aiStatusText
        } else if (eventName === 'done') {
          if (payload.css !== undefined) {
            this.cssText = payload.css
            this.onCssInput()
          }
          publishAiTokenUsage(payload.tokenUsage)
          this.aiStatusText = 'Done!'
        } else if (eventName === 'error') {
          throw new Error(payload.message || 'CSS edit failed')
        }
      }

      return rest
    },
    async readCssStream (response) {
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const chunk = await reader.read()

        if (chunk.done) {
          if (buffer.trim()) this.consumeCssEvents(`${buffer}\n\n`)
          break
        }

        buffer += decoder.decode(chunk.value, { stream: true })
        buffer = this.consumeCssEvents(buffer)
      }
    },
    async runAiEdit () {
      if (!this.aiPrompt.trim() || this.aiLoading) return

      this.aiLoading = true
      this.aiError = null
      this.aiThinking = ''
      this.aiStatusText = 'Starting...'

      const application = this.parentDesigner.$route.params.application
      const pageUniqueTitle = this.parentDesigner.page
        ? this.parentDesigner.page.uniqueTitle
        : null
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), CSS_EDIT_TIMEOUT_MS)

      try {
        const body = {
          prompt: this.aiPrompt.trim(),
          currentCss: this.cssText,
          mode: this.mode,
          pageUniqueTitle,
          componentUniqueTitle: this.componentUniqueTitle || null,
          componentType: this.componentType || null,
          stream: true
        }

        if (this.aiReferenceImage) {
          body.referenceImage = this.aiReferenceImage
        }

        const response = await fetch(
          `${VUE_APP_PROXY_SERVER_URL}/ai-css`,
          {
            method: 'POST',
            credentials: 'include',
            signal: controller.signal,
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'text/event-stream',
              'application-unique-title': application
            },
            body: JSON.stringify(body)
          }
        )

        if (!response.ok) {
          const text = await response.text()
          throw new Error(text || response.statusText)
        }

        await this.readCssStream(response)
      } catch (err) {
        console.log('AI CSS edit failed', err)
        this.aiError = err.name === 'AbortError'
          ? 'CSS edit timed out'
          : err.message
      } finally {
        clearTimeout(timer)
        this.aiLoading = false
      }
    }
  }
}
</script>

<style>
.css-editor-panel {
  position: absolute;
  top: 0;
  left: 0;
  width: 420px;
  height: 100%;
  background-color: #1a1a17;
  border-right: 2px solid #9ad12b;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.5);
}

.css-editor-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background-color: #141412;
  border-bottom: 1px solid #2e2e2b;
  flex-shrink: 0;
}

.css-editor-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #e8e8e6;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.css-editor-type-badge {
  font-size: 0.65rem;
  padding: 2px 6px;
  background-color: #2a2a27;
  color: #9ad12b;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.css-editor-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  margin: 0;
  padding: 0;
  background: none;
  border: none;
  color: #7a7a78;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}
.css-editor-close:hover {
  color: #f82471;
}

.css-editor-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 10px 14px;
  overflow: hidden;
}

.css-editor-help-toggle {
  display: block;
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  font-size: 0.78rem;
  color: #9ad12b;
  cursor: pointer;
  padding: 4px 0;
  user-select: none;
}

.css-editor-help {
  font-size: 0.72rem;
  color: #b0b0ae;
  background-color: #141412;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
  padding: 8px 12px;
  margin: 6px 0;
  max-height: 200px;
  overflow-y: auto;
}
.css-editor-help code {
  color: #9ad12b;
  background-color: #0e0e0c;
  padding: 1px 4px;
  border-radius: 2px;
  font-size: 0.72rem;
}
.css-editor-help ul {
  padding-left: 16px;
  margin: 4px 0;
}
.css-editor-help li {
  margin: 2px 0;
}

.css-editor-info {
  font-size: 0.72rem;
  color: #6a6a67;
  padding: 4px 0 8px;
}
.css-editor-info code {
  color: #9ad12b;
}

.css-editor-textarea {
  flex: 1;
  background-color: #0e0e0c;
  color: #e8e8e6;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
  padding: 10px;
  font-family: 'SF Mono', 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 0.82rem;
  line-height: 1.5;
  resize: none;
  tab-size: 2;
}
.css-editor-textarea:focus {
  outline: none;
  border-color: #9ad12b;
}
.css-editor-textarea::placeholder {
  color: #3a3a37;
}

.css-editor-footer {
  display: flex;
  gap: 8px;
  padding: 10px 14px;
  background-color: #141412;
  border-top: 1px solid #2e2e2b;
  flex-shrink: 0;
}

.css-editor-btn {
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  border: 1px solid transparent;
}

.css-editor-btn-save {
  background-color: #9ad12b;
  color: #0e0e0c;
  font-weight: 600;
}
.css-editor-btn-save:hover {
  background-color: #b8f046;
}

.css-editor-btn-cancel {
  background-color: transparent;
  color: #7a7a78;
  border-color: #2e2e2b;
}
.css-editor-btn-cancel:hover {
  color: #b0b0ae;
  border-color: #4a4a47;
}

.css-ai-section {
  margin-bottom: 8px;
}

.css-ai-header {
  display: block;
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  font-size: 0.78rem;
  color: #d4a0ff;
  cursor: pointer;
  padding: 4px 0;
  user-select: none;
}

.css-ai-body {
  margin-top: 4px;
}

.css-ai-prompt {
  width: 100%;
  background-color: #0e0e0c;
  color: #e8e8e6;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
  padding: 8px;
  font-family: inherit;
  font-size: 0.78rem;
  line-height: 1.4;
  resize: none;
}
.css-ai-prompt:focus {
  outline: none;
  border-color: #d4a0ff;
}
.css-ai-prompt::placeholder {
  color: #4a4a47;
}
.css-ai-prompt:disabled {
  opacity: 0.5;
}

.css-ai-btn {
  margin-top: 6px;
  width: 100%;
  background-color: #2a1a3e;
  color: #d4a0ff;
  border: 1px solid #5a3a7e;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.css-ai-btn:hover:not(:disabled) {
  background-color: #3a2a5e;
  border-color: #7a5a9e;
}
.css-ai-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.css-ai-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid #5a3a7e;
  border-top-color: #d4a0ff;
  border-radius: 50%;
  animation: css-ai-spin 0.6s linear infinite;
}

@keyframes css-ai-spin {
  to { transform: rotate(360deg); }
}

.css-ai-image-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

.css-ai-image-label {
  font-size: 0.72rem;
  color: #8a8a87;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}
.css-ai-image-label:hover:not(.disabled) {
  color: #d4a0ff;
}
.css-ai-image-label.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.css-ai-image-input {
  display: none;
}

.css-ai-image-remove {
  background: none;
  border: none;
  color: #7a7a78;
  font-size: 1rem;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
}
.css-ai-image-remove:hover:not(:disabled) {
  color: #f82471;
}
.css-ai-image-remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.css-ai-image-preview {
  margin-top: 6px;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
  overflow: hidden;
  max-height: 120px;
}
.css-ai-image-preview img {
  display: block;
  width: 100%;
  max-height: 120px;
  object-fit: contain;
  background-color: #0e0e0c;
}

.css-ai-progress {
  margin-top: 8px;
}
.css-ai-status {
  font-size: 0.72rem;
  color: #9ad12b;
}
.css-ai-thoughts {
  margin: 6px 0 0;
  max-height: 180px;
  overflow: auto;
  white-space: pre-wrap;
  color: #8d8d8a;
  background: #141412;
  border: 1px solid #2a2a27;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 0.75rem;
  line-height: 1.45;
  font-family: inherit;
}

.css-ai-error {
  margin-top: 6px;
  font-size: 0.72rem;
  color: #f82471;
  background-color: rgba(248, 36, 113, 0.1);
  border-radius: 3px;
  padding: 4px 8px;
}
</style>
